import { CdkDragPlaceholder } from '@angular/cdk/drag-drop';
import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { injectShellEnvironment, isEmbeddedShellEnvironment, menuBar, MenuBarButton, MenuBarDateNavigator, MenuBarPillSwitch, panel, ShellService } from '@softapps/core';
import { PageComponent, PageHeaderComponent } from '@softapps/core/pages';
import {
  BackNavigable,
  BackNavigationService,
  FilterAndSortComponent,
  FilterConfig,
  menuItems,
  Refreshable,
  RefreshService,
  showRequestErrors,
  SOFTLINE_FEATURE_TITLE,
  SortConfig,
  TitleStore,
} from '@softline/application';
import {
  explore,
  Filter,
  FilterService,
  isDefinedNotEmpty,
  LocalStorageDataAccess,
  Sort,
  SortService,
  Store,
  withFilter,
  withSearch,
  withSort,
} from '@softline/core';
import { translator } from '@softline/ui-core';
import { combineLatestWith, map, Observable } from 'rxjs';
import { AbteilungCardComponent } from '../../components/abteilung-card/abteilung-card.component';
import { AbteilungenService } from '../../services/abteilungen.service';
import { DateNavigatorService } from '../../services/date-navigator.service';
import {
  SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN,
  SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER,
} from '../../statusabfrage.shared';
import { PmsAbteilungStore } from '../../store/abteilung.store';
import { PmsPersonalStore } from '../../store/personal.store';
import { AbteilungUebersicht } from '../../types/abteilung';
import { injectSearchQuery } from '../../utils/inject-search-query';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'soft-abteilungen',
  templateUrl: './abteilungen.component.html',
  styleUrls: ['./abteilungen.component.css'],
  imports: [
    CommonModule,
    RouterLink,
    FilterAndSortComponent,
    AbteilungCardComponent,
    CdkDragPlaceholder,
    MenuBarDateNavigator,
    MenuBarPillSwitch,
    MenuBarButton,
    PageHeaderComponent,
    PageComponent,
  ],
  providers: [DatePipe],
  host: {
    '[style.maxHeight]': 'maxHeightCalc()',
  },
})
export class AbteilungenComponent
  implements OnInit, OnDestroy, BackNavigable, Refreshable
{
  readonly shellEnvironment = injectShellEnvironment();
  readonly shellService = inject(ShellService);

  readonly contentRect = toSignal(this.shellService.contentRect$, {
    initialValue: undefined,
  });

  readonly offsetTop = computed(() => {
    const isEmbeddedContext = isEmbeddedShellEnvironment(this.shellEnvironment);
    const offsetTop = this.contentRect()?.top ?? 0;
    if (isEmbeddedContext) return offsetTop;
    return offsetTop > 90 ? offsetTop : offsetTop + 92; // height of page header with date-navigator = 92px
  });

  readonly maxHeightCalc = computed(() => `calc(100vh - ${this.offsetTop()}px)`);

  private unregisterDateChange?: () => void;

  textFilterFromQueryParam = injectSearchQuery();

  readonly abteilungen$ = this.store.observe(
    SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN,
    PmsAbteilungStore.getters.all
  );
  readonly abteilungen = toSignal(this.abteilungen$);

  readonly explorerAbteilungen = explore(
    this.abteilungen,
    withFilter(),
    withSort({ defaultSort: { property: 'abteilung.bezeichnung', direction: 'asc' } }),
    withSearch({ defaultSearchTerm: this.textFilterFromQueryParam() })
  );

  readonly loading$ = this.store.observe(
    SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN,
    PmsAbteilungStore.getters.loading
  );

  readonly sort$ = this.store.observe(
    SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN,
    PmsAbteilungStore.getters.sort
  );

  readonly filter$ = this.store.observe(
    SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN,
    PmsAbteilungStore.getters.filter
  );

  readonly filteredItems$: Observable<AbteilungUebersicht[]> = this.abteilungen$.pipe(
    combineLatestWith(this.sort$, this.filter$),
    map(([values, sort, filter]) =>
      this.sortService.sort(this.filterService.filter(values, filter), sort)
    )
  );

  filterConfig: FilterConfig = {
    properties: [
      {
        property: 'abteilung',
        title: 'Abteilung ID',
        params: {
          formatFn: (o: any) => `${o.id}`,
        },
      },
      {
        property: 'abteilung.bezeichnung',
        title: 'Abteilung-Bezeichnung',
        params: {
          formatFn: (o: any) => `${o}`,
        },
      },
      {
        property: 'mitarbeiterGesamt',
        title: 'Anzahl Mitarbeiter Gesamt',
      },
      {
        property: 'anwesend',
        title: 'Anwesende Mitarbeiter',
      },
    ],
  };

  sortConfig: SortConfig = {
    properties: [
      {
        property: 'abteilung.id',
        title: 'Abteilung ID',
      },
      {
        property: 'abteilung.bezeichnung',
        title: 'Abteilungs-Bezeichnung',
      },
      {
        property: 'mitarbeiterGesamt',
        title: 'Anzahl Mitarbeiter Gesamt',
      },
      {
        property: 'anwesend',
        title: 'Anwesend',
      },
    ],
  };

  readonly trackByAbteilungId = (_: number, abteilung: AbteilungUebersicht) =>
    abteilung.id;

  t = translator();
  readonly actionBarTemplate = viewChild.required('actionBar', {
    read: TemplateRef,
  });
  readonly trailingActionBarTemplate = viewChild.required('trailingActionBar', {
    read: TemplateRef,
  });

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly store: Store,
    private readonly filterService: FilterService,
    private readonly sortService: SortService,
    private readonly datePipe: DatePipe,
    private readonly dateNavigatorService: DateNavigatorService,
    private readonly backNavigationService: BackNavigationService,
    private readonly refreshService: RefreshService,
    private readonly abteilungenService: AbteilungenService
  ) {
    menuItems(() => [
      {
        name: 'abteilungen',
        type: 'route',
        outlet: 'nav',
        icon: 'fa-regular fa-list',
        title: 'Abteilungen',
        routerLink: ['/statusabfrage'],
      },
    ]);

    menuBar({
      key: 'softapps/pms/statusabfrage/abteilungen',
      title: this.t('Abteilungen'),
      separated: true,
      template: this.actionBarTemplate,
    });
    menuBar({
      key: 'softapps/pms/statusabfrage/abteilungen',
      title: this.t('Abteilungen'),
      separated: true,
      align: 'trailing',
      template: this.trailingActionBarTemplate,
    });

    effect(() => {
      this.viewModeStorage.set({ key: this.viewModeStorageKey }, this.viewMode());
    });
  }

  private readonly viewModeStorage = inject(LocalStorageDataAccess);
  private readonly viewModeStorageKey = 'soft.pms-statusabfrage.abteilungen.viewMode';

  viewMode = signal<'list' | 'grid'>(
    this.viewModeStorage.get<'list' | 'grid'>({ key: this.viewModeStorageKey }) ?? 'list'
  );

  async ngOnInit(): Promise<void> {
    // API Request

    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setSubTitle,
      'Abteilungen'
    );

    this.backNavigationService.set(this);
    this.refreshService.add(this);

    this.unregisterDateChange = this.dateNavigatorService.onDateChange(
      async (date) => await this.onNavigatorChange(date)
    );
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['./../'], { relativeTo: this.activatedRoute });
  }

  ngOnDestroy(): void {
    this.unregisterDateChange?.();

    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setSubTitle, '');

    /*this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      AbteilungenComponent
    );*/

    this.store.commit(
      SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER,
      PmsPersonalStore.mutations.setFilter,
      null
    );

    this.store.commit(
      SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER,
      PmsPersonalStore.mutations.setSort,
      null
    );

    this.backNavigationService.set(undefined);
    this.refreshService.remove(this);
  }

  async refresh(): Promise<void> {
    this.onNavigatorChange(this.dateNavigatorService.date);
  }

  async onNavigatorChange(date: string) {
    this.store.commit(
      SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN,
      PmsAbteilungStore.mutations.clear
    );
    try {
      const stichdatum = this.datePipe.transform(date, 'yyyy-MM-dd');
      await this.abteilungenService.loadAbteilungen(stichdatum);
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  async onFilterChange(filter: Filter | null): Promise<void> {
    this.store.commit(
      SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN,
      PmsAbteilungStore.mutations.setFilter,
      filter
    );
  }

  async onSortChange(sort: Sort<AbteilungUebersicht> | null): Promise<void> {
    this.store.commit(
      SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN,
      PmsAbteilungStore.mutations.setSort,
      sort
    );
  }

  async updateQueryParams(filterText: Event): Promise<void> {
    await this.router.navigate([], {
      queryParams: isDefinedNotEmpty(filterText)
        ? { search: encodeURIComponent((filterText.target as HTMLInputElement).value) }
        : undefined,
      relativeTo: this.activatedRoute,
    });
  }
}
